import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAtom, useAtomValue } from 'jotai'
import { Header } from 'components/common/header/Header'
import { formatDate, formatTime } from 'common/date'
import { Spacer } from 'wiz-front-shared/components/Spacer'
import { notificationStore } from 'store/notificationStore'
import { accessTokenAtom, tokenAnsweredAtom } from 'store/tokenStore'
import { requestApp, WEB_TO_APP } from 'common/webview'
import { pushLocStringsAtom } from 'store/pushLocStringsStore'
import { MESSAGE } from 'repository/types/MESSAGE.types'
import './Notification.css'

interface NotificationGroup {
    date: string
    items: MESSAGE.PushHistItem[]
}

const groupNotificationsByDate = (notifications: MESSAGE.PushHistItem[]): NotificationGroup[] => {
    const groups: Record<string, MESSAGE.PushHistItem[]> = {}

    notifications.forEach((notification) => {
        const dateKey = notification.sentAt.split('T')[0]
        if (!groups[dateKey]) {
            groups[dateKey] = []
        }
        groups[dateKey].push(notification)
    })

    return Object.entries(groups)
        .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
        .map(([date, items]) => ({ date, items }))
}

/**
 * `{{0}}`, `{{1}}` 위치 치환자를 locArgs 로 바꾼다.
 * 인자가 모자라면 빈 문자열로 채워 치환자가 그대로 노출되지 않게 한다.
 */
const formatPushString = (template: string, locArgs: string[] = []): string =>
    template.replace(/\{\{(\d+)\}\}/g, (_, index) => locArgs[Number(index)] ?? '')

/** 언어별 완성 문장 묶음에서 화면 언어에 맞는 문장을 고른다. 없으면 en → ko 순으로 떨어진다. */
const pickLocalizedMessage = (messages: unknown, lang: string): string | undefined => {
    if (!messages || typeof messages !== 'object') return undefined
    const table = messages as Record<string, unknown>
    const pick = (l: string): string | undefined => {
        const value = table[l]
        return typeof value === 'string' && value.trim() ? value : undefined
    }
    return pick(lang) ?? pick('en') ?? pick('ko')
}

/**
 * 알림 본문 결정: 앱이 주입한 문구 맵에 loc-key 가 있으면 그것으로, 없으면 서버 content 로.
 * 서버는 수신자 언어를 모르므로 content 는 기본 언어로 고정돼 있고 폴백 용도다.
 * (브라우저로 직접 열어 맵이 비어 있는 경우도 content 로 떨어진다)
 */
const resolveNotificationBody = (
    item: MESSAGE.PushHistItem,
    locStrings: Record<string, string>,
    currentLang: string,
): string => {
    if (item.data) {
        try {
            const payload = JSON.parse(item.data)
            const template = payload?.bodyLocKey ? locStrings[payload.bodyLocKey] : undefined
            if (template) {
                const locArgs = Array.isArray(payload?.locArgs) ? payload.locArgs.map(String) : []
                return formatPushString(template, locArgs)
            }
            // loc-key 가 없는 알림(AI 가 만든 완성 문장)은 서버가 언어별 문장을 통째로 보낸다.
            //   장소·시각이 박힌 동적 문장이라 loc-key 로 만들 수 없어, 고르는 일만 여기서 한다.
            const localized = pickLocalizedMessage(payload?.messages, currentLang)
            if (localized) return localized
        } catch {
            // 파싱 실패 시 아래 content 폴백
        }
    }
    return item.content ?? ''
}

export const Notification = () => {
    const { t, i18n } = useTranslation()
    const { notificationList, getNotificationList } = notificationStore
    const [notificationListState] = useAtom(notificationList.stateAtom)
    const [dataList, setDataList] = useState<MESSAGE.PushHistItem[]>([])

    const accessToken = useAtomValue(accessTokenAtom)
    const tokenAnswered = useAtomValue(tokenAnsweredAtom)
    const pushLocStrings = useAtomValue(pushLocStringsAtom)

    useEffect(() => {
        if (accessToken) {
            console.log("알림목록 조회")
            getNotificationList({ pageNo: 1, pageSize: 20, statuses: ['SENT'] })
            return
        }
        // 토큰이 없으면 앱에 달라고 요청한다.
        //   토큰은 sessionStorage 에만 있어 페이지가 다시 로드되면 비고, 그때 앱이 보내주는
        //   시점을 놓치면 조회를 못 한 채 멈춘다. 기다리지 말고 직접 끌어온다.
        //   앱은 로그아웃 상태여도 빈 값으로 답하므로 답이 오면 상태가 확정된다.
        requestApp(WEB_TO_APP.REQUEST_TOKEN)
    }, [accessToken])

    useEffect(() => {
        if (notificationListState.status === 'ready' && notificationListState?.data?.data?.list?.length > 0) {
            setDataList(notificationListState.data.data.list)
        }
    }, [notificationListState.status])

    const groupedNotifications = useMemo(() => {
        return groupNotificationsByDate(dataList)
    }, [dataList])

    // 목록 대신 보여줄 안내 문구. null 이면 목록을 그린다.
    //   '알림 없음'은 조회에 성공해서 0건일 때만 쓴다 — 토큰을 못 받아 조회를 못 한 상태와
    //   구분하지 않으면, 앱이 토큰을 놓쳤을 때 알림이 없는 것처럼 보인다.
    const placeholder = useMemo(() => {
        if (!accessToken) {
            // 앱이 답을 줬는데도 토큰이 비었다 = 로그인하지 않은 상태로 확정.
            if (tokenAnswered) { return 'notification.needLogin' }
            // 아직 답이 없다 = 요청해 둔 상태. 여기서 '로그인하세요'라고 하면
            // 로그인돼 있는 사용자에게 거짓말이 된다.
            return 'notification.loading'
        }
        if (notificationListState.isPending || notificationListState.isDefault) {
            return 'notification.loading'
        }
        if (notificationListState.isError) {
            return 'notification.error'
        }
        return groupedNotifications.length === 0 ? 'notification.empty' : null
    }, [accessToken, tokenAnswered, notificationListState.isPending,
        notificationListState.isDefault, notificationListState.isError, groupedNotifications.length])

    return (
        <div className="notification">
            <Header title={t('notification.title')} isBack />
            <div className="notification-list">
                {placeholder ? (
                    <div className="notification-empty">
                        {t(placeholder)}
                    </div>
                ) : (
                    groupedNotifications.map((group) => (
                        <div key={group.date} className="notification-group">
                            <Spacer height={30} />
                            <span className="notification-header">
                                {formatDate(group.date)}
                            </span>
                            <Spacer height={12} />
                            {group.items.map((item, index) => (
                                <div key={item.pushId}>
                                    <div className="notification-item">
                                        <span className="notification-text">
                                            {resolveNotificationBody(item, pushLocStrings, i18n.language)}
                                        </span>
                                        <Spacer height={4} />
                                        <span className="notification-time">
                                            {formatTime(item.sentAt)}
                                        </span>
                                    </div>
                                    {index < group.items.length - 1 && <Spacer height={10} />}
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}
